import { useCommonDependencies } from 'adapter/in/common/commonDependencies';
import { ConfigFieldType, FieldStatus, SelectorFieldDataImpl, SelectorOption, SelectorType } from 'adapter/in/common/form/types';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { isSome } from 'fp-ts/lib/Option';
import { useEffect, useState } from 'react';
import { PresetFieldDependency, ReadPresetFieldCriteriaBuilder } from 'use-case/presets/fields/read/readPresetFieldCriteria';
import { useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import { parsePeriodCodeToPeriodTemplateState } from 'adapter/in/common/components/PeriodSelector/periodSelectorUtils';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { isSelectorValueValid } from 'adapter/in/common/form/Selector';

export const getInitialSelectorValues = (value: SelectorFieldDataImpl, options: SelectorOption[]) =>
    value.getInitialValue() !== undefined ? options.filter((option) => value.getInitialValue().includes(option.value)) : [];

const getDefaultValues = (options: SelectorOption[], minSelectionLength?: number) => {
    if (minSelectionLength !== undefined && minSelectionLength === 0) return [];
    const selectedValues = options.filter((option) => option.selected);
    return selectedValues.length > 0 ? selectedValues : [options[0]];
};

export const mapSelectorValueToOption = (option: SelectorValue, type: SelectorType): SelectorOption => ({
    type: SelectorType.SELECT,
    value: option.id,
    label:
        option.properties && option.properties.shortLabel !== '' && option.properties.shortLabel !== undefined
            ? option.properties.shortLabel
            : option.label,
    id: option.id,
    color: 'gray300',
    tooltip: option.label,
    icon: option.properties ? option.properties.icon : undefined,
    isDisabled: option.properties ? option.properties.isDisabled : undefined,
    disabled: option.properties ? option.properties.disabled : undefined,
    selected: option.selected,
    state: type === SelectorType.PERIOD ? parsePeriodCodeToPeriodTemplateState(option.id) : undefined,
});

export const useBaseDashboardsApiSelector = <POption>(
    name: string,
    type: SelectorType,
    configType: ConfigFieldType,
    pageSize: number,
    value: SelectorFieldDataImpl,
    shouldLoad: boolean,
    dependantValues: PresetFieldDependency[],
    onChange: (value: SelectorFieldDataImpl) => void
) => {
    const { preset, widgetType } = useCurrentWidgetConfiguration();
    const { readPresetField: readPresetFieldUseCase } = useCommonDependencies();
    const { isLoading, data: presetFieldResult, execute: readPresetField } = useCase(readPresetFieldUseCase, false);
    const [options, setOptions] = useState<POption[]>([]);
    const shouldLoadOptions =
        value && [FieldStatus.INIT, FieldStatus.INITIAL_VALUE, FieldStatus.INVALID, FieldStatus.RESET].includes(value.status) && shouldLoad;
    const hasInitialValue = () => {
        let initialValue = value.getInitialValue()[0];
        if (Array.isArray(initialValue)) {
            initialValue = initialValue[0];
        }
        return (
            (value.status === FieldStatus.INITIAL_VALUE ||
                value.status === FieldStatus.DEFAULT_DATA ||
                value.status === FieldStatus.RESET) &&
            !!initialValue
        );
    };
    useEffect(() => {
        if (shouldLoadOptions) {
            readPresetField(
                ReadPresetFieldCriteriaBuilder.create()
                    .withPreset(preset.key)
                    .withDependencies(dependantValues.map((dep) => new PresetFieldDependency(dep.key.split('-')[0], dep.value)))
                    .withWidgetType(widgetType)
                    .withField(name.split('-')[0])
                    .withPageSize(pageSize ?? 0)
                    .build()
            );
        }
    }, [shouldLoadOptions]);

    useEffect(() => {
        if (isSome(presetFieldResult)) {
            const optionsToSet = (presetFieldResult.value.values as SelectorValue[]).map((option) =>
                mapSelectorValueToOption(option, type)
            );
            setOptions((optionsToSet as unknown) as POption[]);
            if (optionsToSet.length === 0 && presetFieldResult.value.minSelectionLength && presetFieldResult.value.minSelectionLength > 0) {
                onChange(buildSelectorFieldData(FieldStatus.INVALID, value.initialValue, configType, []));
            } else {
                switch (type) {
                    case SelectorType.PERIOD: {
                        const foundValue = optionsToSet.find((v) => v.selected);
                        const initialDefaultValue =
                            hasInitialValue() &&
                            [FieldStatus.HAS_VALUE, FieldStatus.INITIAL_VALUE, FieldStatus.RESET].includes(value.status)
                                ? (value.getInitialValue() as Array<string>)[0]
                                : foundValue?.id || presetFieldResult.value.values[0].id;

                        onChange(
                            buildSelectorFieldData(FieldStatus.HAS_VALUE, value.initialValue, configType, [
                                {
                                    type: SelectorType.SELECT,
                                    value: initialDefaultValue,
                                    label: initialDefaultValue,
                                    color: initialDefaultValue,
                                    id: initialDefaultValue,
                                },
                            ])
                        );
                        break;
                    }
                    case SelectorType.FIELD_GROUP:
                    case SelectorType.SELECT:
                    default: {
                        const initialValues = getInitialSelectorValues(value, optionsToSet);
                        const selectedValueToSet =
                            initialValues.length > 0
                                ? initialValues
                                : getDefaultValues(optionsToSet, presetFieldResult.value.minSelectionLength);
                        onChange(
                            buildSelectorFieldData(
                                isSelectorValueValid(
                                    presetFieldResult.value.minSelectionLength ?? 1,
                                    presetFieldResult.value.maxSelectionLength ?? 1
                                )(selectedValueToSet)
                                    ? FieldStatus.HAS_VALUE
                                    : FieldStatus.INVALID,
                                selectedValueToSet.map((selValue) => selValue.value).join(','),
                                configType,
                                selectedValueToSet
                            )
                        );
                        break;
                    }
                }
            }
        }
    }, [presetFieldResult]);

    return {
        isLoading,
        options: isSome(presetFieldResult) ? options : [],
        maxSelectionLength:
            isSome(presetFieldResult) && presetFieldResult.value.maxSelectionLength ? presetFieldResult.value.maxSelectionLength : 1,
        minSelectionLength:
            isSome(presetFieldResult) && presetFieldResult.value.minSelectionLength ? presetFieldResult.value.minSelectionLength : 0,
    };
};
